/**
 * Firebase Environment Utilities
 * Handles dynamic function URL generation across multiple environments
 */

// Environment detection
//
// Derived from the BUILD, not from the URL it happens to be served at. The
// build already knows which Firebase project it is for — `use-auth.js:34`
// initialises the real Firebase app from VITE_FIREBASE_PROJECT_ID — so reading
// the same variable here makes this module agree with the app it describes by
// construction, instead of guessing and sometimes disagreeing.
//
// WHY THE HOSTNAME TEST WAS REPLACED. It read `hostname.includes("dev")` and
// fell through to "production" for anything unmatched, which means the answer
// depended on the DOMAIN NAME the host assigned rather than on what was built:
// a dev bundle served from any hostname without the substring "dev" or "qa" in
// it — a Vercel preview URL, a *.web.app fallback domain, an IP, a custom
// domain — silently reported "production", and `buildFunctionUrl()` then
// resolved to https://app.t3books.com/api. That is a dev test-drive calling
// PRODUCTION Cloud Functions, and it is invisible: nothing errors, the calls
// succeed against the wrong project. The same fall-through flipped every
// feature gate built on `getProjectId() !== PROD_PROJECT_ID`
// (src/catalog/featureGate.js, onboarding/llm/featureGate.js) to OFF, so the
// features under test would quietly not render. Both failure modes needed the
// deployment to be named a particular way to avoid them; neither is acceptable
// as a safety property.
//
// The localhost check stays FIRST and stays hostname-based on purpose: "local"
// selects the Functions EMULATOR (127.0.0.1:5001), which is a property of where
// the code is running, not of which project it was built for — a development
// build is exactly what one runs against the emulator.
const PROJECT_ENVIRONMENTS = {
  "dive-dev-f9d3f": "dev",
  "dive-qa": "qa",
  "arcwise-ad50c": "production",
};

const getEnvironment = () => {
  const hostname = window.location.hostname;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "local";
  }

  // Unknown or unset project id falls back to "dev", never "production": the
  // costs are not symmetric. Guessing dev in a prod build degrades admin tools
  // and feature gates in an environment where someone will notice immediately;
  // guessing production in a dev build points them at live customer data.
  return PROJECT_ENVIRONMENTS[import.meta.env.VITE_FIREBASE_PROJECT_ID] ?? "dev";
};

// Environment configuration
const environmentConfig = {
  local: {
    functionsUrl: "http://127.0.0.1:5001/dive-dev-f9d3f/us-central1",
    region: "us-central1",
    projectId: "dive-dev-f9d3f",
  },
  dev: {
    functionsUrl: "https://dev.t3books.com/api",
    region: "us-central1",
    projectId: "dive-dev-f9d3f",
  },
  qa: {
    functionsUrl: "https://qa.t3books.com/api",
    region: "us-central1",
    projectId: "dive-qa",
  },
  production: {
    functionsUrl: "https://app.t3books.com/api",
    region: "us-central1",
    projectId: "arcwise-ad50c",
  },
};

/**
 * Get the current environment configuration
 * @returns {Object} Environment config object
 */
export const getCurrentEnvironment = () => {
  const env = getEnvironment();
  return {
    name: env,
    ...environmentConfig[env],
  };
};

/**
 * Build a Firebase function URL for the current environment
 * @param {string} functionName - Name of the function (e.g., 'create_link_token')
 * @returns {string} Complete function URL
 */
export const buildFunctionUrl = (functionName) => {
  const config = getCurrentEnvironment();
  return `${config.functionsUrl}/${functionName}`;
};

/**
 * Get multiple function URLs at once
 * @param {string[]} functionNames - Array of function names
 * @returns {Object} Object with functionName: url mappings
 */
export const buildFunctionUrls = (functionNames) => {
  const urls = {};
  functionNames.forEach((name) => {
    urls[name] = buildFunctionUrl(name);
  });
  return urls;
};

/**
 * Get environment info for debugging
 * @returns {Object} Current environment details
 */
export const getEnvironmentInfo = () => {
  const env = getCurrentEnvironment();
  return {
    environment: env.name,
    hostname: window.location.hostname,
    functionsUrl: env.functionsUrl,
    projectId: env.projectId,
    isLocal: env.name === "local",
  };
};

// Export individual functions that might be commonly used
export const isLocalEnvironment = () =>
  getCurrentEnvironment().name === "local";
export const getProjectId = () => getCurrentEnvironment().projectId;
export const getFunctionsBaseUrl = () => getCurrentEnvironment().functionsUrl;
